import { memo, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import Breadcrumbs from "@mui/material/Breadcrumbs";
import Breadcrumb from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import {
  Connection,
  loadAllConnections,
  useConnectionStore,
} from "stores/ConnectionStore";
import { SecretState, swrFetcher } from "stores/lib";
import { useOrganizationStore } from "stores/OrganizationStore";

import asyncOrSwim from "utility/async_or_swim";
import { captureSentryException } from "utility/capture_sentry_exception";

import ServiceConnectionInstructions from "components/ServiceConnectionInstructions";

import ErrorPhase from "./Phase/ErrorPhase";
import RunCommandPhase from "./Phase/RunCommandPhase";
import SuccessPhase from "./Phase/SuccessPhase";
import TestingPhase from "./Phase/TestingPhase";

// -----------------------------------------------------------------------------

const ProvisionURL = "api/organization/azure/provision";

enum Phase {
  DeleteAllBadAzureConnections = -1,
  LoadingNonce = 1,
  RunPowerShellCommand = 2,
  TestingConnection = 3,
  DisplayingResults = 4,
}

// -----------------------------------------------------------------------------

const GoogleConnectSetupPage = memo(function GoogleConnectSetupPage() {
  const [orgStore] = useOrganizationStore();
  const [, connectionActions] = useConnectionStore();

  const [phase, setPhase] = useState<Phase>(Phase.LoadingNonce);
  const [contactingServer, setContactingServer] = useState<boolean>(false);
  const [nonce, setNonce] = useState<string | undefined>(undefined);
  const [connectionUuid, setConnectionUuid] = useState<string | undefined>(
    undefined
  );
  const [results, setResults] = useState<{
    success: boolean;
    message?: string;
  }>({ success: false, message: "" });

  //
  useEffect(() => {
    switch (phase) {
      case Phase.DeleteAllBadAzureConnections:
        return !contactingServer
          ? asyncOrSwim(
              async () => {
                setContactingServer(true);

                // delete bad azure connections
                if (phase === Phase.DeleteAllBadAzureConnections) {
                  const badAzureConnections = await loadBadAzureConnections();
                  for (const connection of badAzureConnections) {
                    await new Promise((resolve, reject) => {
                      (async () => {
                        connectionActions.deleteConnection({
                          uuid: connection.uuid,
                          onSuccess: resolve,
                          onError: reject,
                        });
                      })();
                    });
                  }
                }

                //
                setContactingServer(false);
                setPhase(Phase.LoadingNonce);
              },
              (error) => {
                captureSentryException(
                  error,
                  "Failed to delete bad Azure connections"
                );
                setResults({
                  success: false,
                  message:
                    "There was an error contacting ThreatKey. Please try again.",
                });
                setPhase(Phase.DisplayingResults);
                setNonce(undefined);
                setConnectionUuid(undefined);
                setContactingServer(false);
              }
            )
          : undefined;
      case Phase.LoadingNonce:
        return !nonce && !contactingServer
          ? asyncOrSwim(
              async () => {
                setContactingServer(true);
                const response = await swrFetcher(ProvisionURL, {
                  method: "POST",
                });
                setPhase(Phase.RunPowerShellCommand);
                setNonce(response.nonce);
                setConnectionUuid(response.connection_uuid);
                setContactingServer(false);
              },
              (error) => {
                captureSentryException(error, "Failed to load Azure nonce");
                setResults({
                  success: false,
                  message:
                    "There was an error contacting ThreatKey. Please try again.",
                });
                setPhase(Phase.DisplayingResults);
                setNonce(undefined);
                setConnectionUuid(undefined);
                setContactingServer(false);
              }
            )
          : undefined;
    }
  }, [phase, nonce, contactingServer, connectionActions]);

  //
  function testConnection() {
    setPhase(Phase.TestingConnection);
    asyncOrSwim(
      async () => {
        setContactingServer(true);
        // if a user is too quick to press the button, the connection's secret might not be ready yet. Retry a few times to make sure.
        let success = false;
        for (let i = 0; i < 2; i++) {
          const connections = await loadAllConnections();
          const connection = connections.get(connectionUuid as string);
          if (!connection) {
            await new Promise((resolve) => setTimeout(resolve, 1500));
            continue;
          }

          success = connection.clientSecret?.state === SecretState.CORRECT;
          if (success) {
            break;
          }
        }
        if (!success) {
          throw new Error("Couldn't find Microsoft Azure connection");
        }
        setContactingServer(false);
        setResults({
          success,
          message: success
            ? undefined
            : "Could not find a properly connected Microsoft Azure connection. Please try again.",
        });
        setPhase(Phase.DisplayingResults);
      },
      (error) => {
        captureSentryException(error, "Failed testing Azure connection");
        setContactingServer(false);
        setResults({
          success: false,
          message:
            "There was an error testing the connection. Please try again.",
        });
        setPhase(Phase.DisplayingResults);
      }
    );
  }

  function deleteAllBadConnectionsAndRestartConfiguration() {
    setNonce(undefined);
    setConnectionUuid(undefined);
    setPhase(Phase.DeleteAllBadAzureConnections);
  }

  //
  const powerShellCommand = nonce
    ? `& ([scriptblock]::Create((irm
    https://s3.amazonaws.com/threatkey-assets/azure/Provision-ThreatKeyAzure.ps1)))
    -ThreatKeyOrganizationId "${orgStore.uuid}" -ThreatKeyNonce "
    ${nonce}" -ServicePrincipalPermissionSet ReadOnly`
    : "";

  //
  return (
    <>
      <Breadcrumbs>
        <Breadcrumb
          underline="none"
          color="inherit"
          component={Link}
          to="/environment/sources"
        >
          Environment
        </Breadcrumb>
        <Breadcrumb
          underline="none"
          color="inherit"
          component={Link}
          to="/environment/sources"
        >
          Sources
        </Breadcrumb>
        <span />
      </Breadcrumbs>
      <ServiceConnectionInstructions label="Microsoft Azure Connection">
        <Stack spacing={2}>
          <Typography variant="body1" fontWeight="bold">
            How the ThreatKey for Microsoft Azure connection works:
          </Typography>
          <Typography variant="body1">
            Connect your ThreatKey account with Microsoft Azure in a just a few
            easy steps. Once connected, you can see all of your results on the
            Findings page.
          </Typography>
        </Stack>
      </ServiceConnectionInstructions>

      <RunCommandPhase
        shown={phase <= Phase.RunPowerShellCommand}
        loading={contactingServer}
        command={powerShellCommand}
        onNext={testConnection}
      />
      <TestingPhase shown={phase === Phase.TestingConnection} />
      <ErrorPhase
        shown={phase === Phase.DisplayingResults && !results.success}
        onNext={deleteAllBadConnectionsAndRestartConfiguration}
        message={results?.message}
      />
      <SuccessPhase
        shown={phase === Phase.DisplayingResults && results.success}
      />
    </>
  );
});

export default GoogleConnectSetupPage;

// -----------------------------------------------------------------------------

async function loadBadAzureConnections() {
  return Object.values(await loadAllConnections()).filter(
    outGoodAzureConnections
  );
}

function outGoodAzureConnections(connection: Connection) {
  return (
    connection.kind === "azure_sp" &&
    connection.clientSecret?.state !== SecretState.CORRECT
  );
}
